import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useMediaQuery from '../../hooks/useMediaQuery';
import useCoarsePointer from '../../hooks/useCoarsePointer';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * DayTimeline — reusable vertical day-at-a-glance agenda for ONE doctor / ONE date
 *
 * Two modes:
 *   • mode="pick"  — interactive booking picker. Tap an open spot to set the
 *                    start (snaps to 5 min); drag the bottom handle of the
 *                    proposed block to resize. Tapping an EXISTING block opens
 *                    its detail popover (so touch users can read it).
 *   • mode="view"  — read-at-a-glance agenda. Tap a block to open its actions.
 *
 * In both modes, hovering a block (desktop) shows a rich detail popover with
 * patient, time, duration, type/procedure, status and notes.
 *
 * Specialty-neutral (Consultation / Procedure). Lifecycle status on each block:
 * BOOKED → CHECKED_IN → IN_PROGRESS → COMPLETED. Overlapping appointments are
 * lane-packed so concurrent blocks render side-by-side.
 * ═══════════════════════════════════════════════════════════════════════
 */

const START_MIN = 0;        // 12 AM — full 24h day (night/emergency support)
const END_MIN = 24 * 60;    // 12 AM next day
const PX_PER_MIN = 1;       // 60px per hour — roomy enough to read + tap
const SNAP = 5;
const MIN_DUR = 5;
const MIN_LAYOUT_MIN = 30;  // floor for a block's *rendered/layout* footprint
const LABEL_GUTTER = 46;    // px reserved on the left for hour labels
const DEFAULT_DUR = { CONSULTATION: 5, PROCEDURE: 45 };

// ── time helpers ─────────────────────────────────────────────────────
export function parseHHMM(str) {
  if (!str || typeof str !== 'string') return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(str);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

export function fmtMin(min) {
  let h = Math.floor(min / 60);
  const x = ((min % 60) + 60) % 60;
  const p = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${String(x).padStart(2, '0')} ${p}`;
}

export function durationOf(appt) {
  const d = appt?.duration_minutes;
  if (d != null && !Number.isNaN(Number(d))) return Number(d);
  return DEFAULT_DUR[appt?.appointment_type] ?? DEFAULT_DUR.CONSULTATION;
}

export function typeLabelOf(appt) {
  if (appt?.appointment_type === 'BLOCK') return appt.label || 'Blocked';
  if (appt?.appointment_type !== 'PROCEDURE') return 'Consultation';
  if (Array.isArray(appt.procedures) && appt.procedures.length) {
    const names = appt.procedures.map((p) => p.name).filter(Boolean).join(', ');
    return names ? `Procedure: ${names}` : 'Procedure';
  }
  return appt.procedure_name ? `Procedure: ${appt.procedure_name}` : 'Procedure';
}

function statusLabelOf(status) {
  return ({ CHECKED_IN: 'Checked in', IN_PROGRESS: 'In progress', COMPLETED: 'Completed' }[status]) || 'Booked';
}

/**
 * Resolve the status to display for an appointment, preferring the linked
 * visit's LIVE status (visit_status from the server) over the appointment's
 * own status. This makes completion — and resume — always accurate.
 */
export function effectiveStatus(appt) {
  const vs = appt?.visit_status;
  if (vs === 'COMPLETED') return 'COMPLETED';
  if (vs === 'IN_PROGRESS') return 'IN_PROGRESS';
  if (vs === 'QUEUE') return 'CHECKED_IN';
  return appt?.status;
}

// Statuses that no longer occupy the slot — shown struck-through, never block a re-book.
export const DEAD_APPT_STATUS = new Set(['CANCELLED', 'EXPIRED', 'NO_SHOW']);

/** First existing appointment whose real window overlaps [startMin, startMin+durMin). */
export function findClash(appointments, startMin, durMin) {
  if (startMin == null) return null;
  const end = startMin + (durMin || MIN_DUR);
  for (const a of appointments || []) {
    if (DEAD_APPT_STATUS.has(a.status)) continue; // a cancelled slot is free to re-book
    const s = parseHHMM(a.appointment_time);
    if (s == null) continue;
    const e = s + durationOf(a);
    if (s < end && e > startMin) return a;
  }
  return null;
}

// ── lane-packing (uses LAYOUT footprint so short blocks don't visually collide) ──
function packLanes(items) {
  const sorted = [...items].sort((a, b) => a.start - b.start || a.layoutEnd - b.layoutEnd);
  const result = [];
  let cluster = [];
  let clusterMaxEnd = -1;

  const flush = () => {
    const laneEnds = [];
    cluster.forEach((it) => {
      let lane = laneEnds.findIndex((e) => e <= it.start);
      if (lane === -1) { lane = laneEnds.length; laneEnds.push(it.layoutEnd); }
      else laneEnds[lane] = it.layoutEnd;
      it._lane = lane;
    });
    const lanes = laneEnds.length || 1;
    cluster.forEach((it) => { it._lanes = lanes; result.push(it); });
    cluster = [];
    clusterMaxEnd = -1;
  };

  sorted.forEach((it) => {
    if (cluster.length && it.start >= clusterMaxEnd) flush();
    cluster.push(it);
    clusterMaxEnd = Math.max(clusterMaxEnd, it.layoutEnd);
  });
  if (cluster.length) flush();
  return result;
}

// ── clearer palette (distinct in both themes) ────────────────────────
function statusViz(status, type, isDark, blocking) {
  const proc = isDark
    ? { bg: 'rgba(217,119,6,0.45)', border: '#fbbf24', text: '#fef3c7' }
    : { bg: '#fde68a', border: '#d97706', text: '#78350f' };
  const cons = isDark
    ? { bg: 'rgba(13,148,136,0.45)', border: '#2dd4bf', text: '#ccfbf1' }
    : { bg: '#99f6e4', border: '#0d9488', text: '#134e4a' };
  const inprog = isDark
    ? { bg: 'rgba(79,70,229,0.5)', border: '#818cf8', text: '#e0e7ff' }
    : { bg: '#c7d2fe', border: '#4f46e5', text: '#312e81' };

  const block = isDark
    ? { bg: 'rgba(100,116,139,0.4)', border: '#94a3b8', text: '#e2e8f0' }
    : { bg: '#e2e8f0', border: '#94a3b8', text: '#334155' };
  const redBlock = isDark
    ? { bg: 'rgba(220,38,38,0.4)', border: '#ef4444', text: '#fecaca' }
    : { bg: '#fee2e2', border: '#dc2626', text: '#991b1b' };

  if (type === 'BLOCK') return { ...(blocking ? redBlock : block), badge: null, opacity: 1 };

  // Cancelled / expired / no-show — keep on the calendar, struck-through + muted.
  if (DEAD_APPT_STATUS.has(status)) {
    const dead = isDark
      ? { bg: 'rgba(107,114,128,0.22)', border: '#6b7280', text: '#9ca3af' }
      : { bg: '#f1f5f9', border: '#94a3b8', text: '#94a3b8' };
    const label = status === 'NO_SHOW' ? 'no-show' : status === 'EXPIRED' ? 'expired' : 'cancelled';
    return { ...dead, badge: { label: `✕ ${label}`, bg: '#6b7280', fg: '#fff' }, opacity: 0.7, strike: true };
  }

  let c = type === 'PROCEDURE' ? proc : cons;
  let badge = null;
  let opacity = 1;
  if (status === 'IN_PROGRESS') { c = inprog; badge = { label: '▶', bg: '#4f46e5', fg: '#fff' }; }
  else if (status === 'CHECKED_IN') { badge = { label: '✓ in', bg: '#059669', fg: '#fff' }; }
  else if (status === 'COMPLETED') { badge = { label: '✓ done', bg: '#64748b', fg: '#fff' }; opacity = 0.55; }
  return { ...c, badge, opacity, strike: false };
}

function LegendSwatch({ color, border, children }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
      <span style={{ width: '0.75rem', height: '0.75rem', borderRadius: '0.1875rem', background: color, border: `1px solid ${border}` }} />
      {children}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
export default function DayTimeline({
  appointments = [],
  date,
  isDark = false,
  mode = 'view',
  selectedTime = null,
  proposedDurationMinutes = 5,
  patientLabel = 'New patient',
  onPickTime,
  onResizeDuration,
  onSelectAppointment,
  onEmptyTap,
  onClashChange,
}) {
  const trackRef = useRef(null);
  const scrollRef = useRef(null);
  const draggingRef = useRef(false);
  const [detail, setDetail] = useState(null); // { appt, x, y, pinned }

  // Responsive: tighter gutter on phones, bigger handle on touch devices.
  const isNarrow = useMediaQuery('(max-width: 640px)');
  const coarse = useCoarsePointer();
  const gutter = isNarrow ? 34 : LABEL_GUTTER;

  const totalHeight = (END_MIN - START_MIN) * PX_PER_MIN;
  const yOf = useCallback((min) => (min - START_MIN) * PX_PER_MIN, []);

  const { isToday, nowMin } = useMemo(() => {
    const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
    const istNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    return { isToday: date === todayStr, nowMin: istNow.getHours() * 60 + istNow.getMinutes() };
  }, [date]);

  const selMin = parseHHMM(selectedTime);
  const propDur = Math.max(MIN_DUR, proposedDurationMinutes || MIN_DUR);

  const blocks = useMemo(() => {
    const items = (appointments || [])
      .map((a) => {
        const start = parseHHMM(a.appointment_time);
        if (start == null) return null;
        const end = start + durationOf(a);
        const layoutEnd = start + Math.max(durationOf(a), MIN_LAYOUT_MIN);
        return { ...a, start, end, layoutEnd };
      })
      .filter(Boolean);
    return packLanes(items);
  }, [appointments]);

  const clash = useMemo(
    () => (mode === 'pick' ? findClash(appointments, selMin, propDur) : null),
    [mode, appointments, selMin, propDur],
  );
  useEffect(() => { if (mode === 'pick' && onClashChange) onClashChange(clash); }, [clash, mode, onClashChange]);

  useEffect(() => {
    if (!scrollRef.current) return;
    // Full 24h track: open near "now" (today) or the earliest booking, else 8 AM —
    // so a daytime clinic isn't staring at the empty small hours, while nights
    // remain reachable by scrolling for emergencies.
    let target = 8 * 60;
    if (isToday && nowMin > START_MIN) target = nowMin;
    else if (blocks.length) target = Math.min(...blocks.map((b) => b.start));
    scrollRef.current.scrollTop = Math.max(0, yOf(target) - 140);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── interactions ───────────────────────────────────────────────────
  const minFromClientY = useCallback((clientY) => {
    const rect = trackRef.current.getBoundingClientRect();
    const min = START_MIN + (clientY - rect.top) / PX_PER_MIN;
    return Math.round(min / SNAP) * SNAP;
  }, []);

  const handleTrackClick = useCallback((e) => {
    if (draggingRef.current) return;
    if (e.target.dataset && e.target.dataset.role === 'resize') return;
    let min = minFromClientY(e.clientY);
    if (mode === 'pick') {
      if (!onPickTime) return;
      min = Math.max(START_MIN, Math.min(END_MIN - propDur, min));
    } else if (onEmptyTap) {
      // view mode: tapping an empty slot opens the block creator — snap to a
      // clean half-hour (7:12 → 7:00, 7:40 → 7:30), not 5-min granularity.
      min = Math.floor(min / 30) * 30;
      min = Math.max(START_MIN, Math.min(END_MIN - 30, min));
    } else {
      return;
    }
    const h = Math.floor(min / 60);
    const m = min % 60;
    const hhmm = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    if (mode === 'pick') onPickTime(hhmm); else onEmptyTap(hhmm);
  }, [mode, onPickTime, onEmptyTap, minFromClientY, propDur]);

  useEffect(() => {
    if (mode !== 'pick') return undefined;
    const onMove = (ev) => {
      if (!draggingRef.current) return;
      ev.preventDefault();
      const clientY = ev.touches ? ev.touches[0].clientY : ev.clientY;
      let endMin = minFromClientY(clientY);
      let dur = Math.max(MIN_DUR, endMin - (selMin ?? START_MIN));
      dur = Math.min(dur, END_MIN - (selMin ?? START_MIN));
      if (onResizeDuration) onResizeDuration(dur);
    };
    const onUp = () => { draggingRef.current = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchend', onUp);
    };
  }, [mode, minFromClientY, selMin, onResizeDuration]);

  const startDrag = useCallback((e) => { e.preventDefault(); e.stopPropagation(); draggingRef.current = true; }, []);

  // detail popover position from a block element
  const showDetailFromEl = (appt, el, pinned) => {
    const r = el.getBoundingClientRect();
    const x = Math.min(r.right + 8, (window.innerWidth || 360) - 252);
    const y = Math.max(8, Math.min(r.top, (window.innerHeight || 640) - 170));
    setDetail({ appt, x, y, pinned });
  };

  const handleBlockClick = (e, b) => {
    e.stopPropagation();
    if (mode === 'view') { if (onSelectAppointment) onSelectAppointment(b); return; }
    // pick mode: tapping an existing block reveals its details (does NOT set time)
    showDetailFromEl(b, e.currentTarget, true);
  };

  // ── styles ─────────────────────────────────────────────────────────
  const gridColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)';
  const labelColor = isDark ? '#9ca3af' : '#9ca3af';
  const innerLeft = `${gutter}px`;
  const empty = blocks.length === 0;

  const legend = (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: '0.5rem 0.875rem',
      padding: '0.375rem 0.25rem', fontSize: '0.625rem',
      color: isDark ? '#cbd5e1' : '#475569',
    }}>
      <LegendSwatch color={isDark ? 'rgba(13,148,136,0.45)' : '#99f6e4'} border={isDark ? '#2dd4bf' : '#0d9488'}>Consultation</LegendSwatch>
      <LegendSwatch color={isDark ? 'rgba(217,119,6,0.45)' : '#fde68a'} border={isDark ? '#fbbf24' : '#d97706'}>Procedure</LegendSwatch>
      <LegendSwatch color={isDark ? 'rgba(79,70,229,0.5)' : '#c7d2fe'} border={isDark ? '#818cf8' : '#4f46e5'}>In progress</LegendSwatch>
      <LegendSwatch color={isDark ? 'rgba(100,116,139,0.4)' : '#e2e8f0'} border="#94a3b8">Blocked</LegendSwatch>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
        <span style={{ borderRadius: '0.1875rem', padding: '0 0.1875rem', fontSize: '0.5rem', fontWeight: 700, background: '#059669', color: '#fff' }}>✓ in</span>Checked in
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
        <span style={{ borderRadius: '0.1875rem', padding: '0 0.1875rem', fontSize: '0.5rem', fontWeight: 700, background: '#64748b', color: '#fff' }}>✓ done</span>Completed
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
        <span style={{ textDecoration: 'line-through', color: isDark ? '#9ca3af' : '#94a3b8' }}>Aa</span>Cancelled
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
        <span style={{ width: '1rem', borderTop: '2px solid #ef4444' }} />now
      </span>
    </div>
  );

  return (
    <div>
      {legend}
      <div
        ref={scrollRef}
        style={{
          maxHeight: mode === 'pick' ? 'min(60vh, 460px)' : 'min(70vh, 600px)',
          overflowY: 'auto',
          overflowX: 'hidden',
          width: '100%',
          boxSizing: 'border-box',
          borderRadius: '0.75rem',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
          background: isDark ? 'rgba(255,255,255,0.02)' : '#ffffff',
          padding: '0.25rem 0.5rem 0.5rem',
        }}
      >
        <div
          ref={trackRef}
          onClick={handleTrackClick}
          style={{
            position: 'relative',
            height: `${totalHeight}px`,
            cursor: mode === 'pick' ? 'crosshair' : (onEmptyTap ? 'copy' : 'default'),
            userSelect: 'none',
          }}
        >
          {/* hour gridlines + labels */}
          {Array.from({ length: (END_MIN - START_MIN) / 60 + 1 }, (_, i) => {
            const min = START_MIN + i * 60;
            const top = yOf(min);
            return (
              <React.Fragment key={min}>
                <div style={{ position: 'absolute', left: innerLeft, right: 0, top: `${top}px`, borderTop: `1px solid ${gridColor}` }} />
                <div style={{ position: 'absolute', left: 0, top: `${top - 6}px`, fontSize: isNarrow ? '0.5625rem' : '0.625rem', color: labelColor, width: `${gutter - 6}px`, textAlign: 'right' }}>
                  {fmtMin(min % 1440).replace(':00', '')}
                </div>
              </React.Fragment>
            );
          })}

          {/* greyed past + now line (today) */}
          {isToday && (
            <>
              <div style={{
                position: 'absolute', left: innerLeft, right: 0, top: 0,
                height: `${Math.max(0, yOf(nowMin))}px`,
                background: isDark ? 'rgba(0,0,0,0.28)' : 'rgba(15,23,42,0.05)', pointerEvents: 'none',
              }} />
              <div style={{ position: 'absolute', left: innerLeft, right: 0, top: `${yOf(nowMin)}px`, borderTop: '2px solid #ef4444', zIndex: 20, pointerEvents: 'none' }}>
                <span style={{ position: 'absolute', right: 0, top: '-8px', fontSize: '0.5625rem', background: '#ef4444', color: '#fff', padding: '0 0.25rem', borderRadius: '0.25rem' }}>now</span>
              </div>
            </>
          )}

          {empty && (
            <div style={{
              position: 'absolute', left: innerLeft, right: 0,
              top: `${yOf(isToday && nowMin > START_MIN ? nowMin : 9 * 60)}px`,
              textAlign: 'center', fontSize: '0.75rem', color: isDark ? '#9ca3af' : '#6b7280', pointerEvents: 'none',
            }}>
              No appointments yet{mode === 'pick' ? ' — tap to pick a time' : ''}
            </div>
          )}

          {/* appointment blocks */}
          {blocks.map((b, idx) => {
            const isClashed = mode === 'pick' && clash && b.start === parseHHMM(clash.appointment_time)
              && b.appointment_time === clash.appointment_time;
            const v = statusViz(b.status, b.appointment_type, isDark, b.blocking);
            const laneW = `calc((100% - ${gutter}px) / ${b._lanes})`;
            const left = `calc(${gutter}px + ${b._lane} * ${laneW})`;
            const height = (b.layoutEnd - b.start) * PX_PER_MIN - 2;
            const twoLine = height >= 30;
            return (
              <div
                key={b.appointment_id || `${b.appointment_time}-${idx}`}
                onClick={(e) => handleBlockClick(e, b)}
                onMouseEnter={(e) => { if (!detail?.pinned) showDetailFromEl(b, e.currentTarget, false); }}
                onMouseLeave={() => setDetail((d) => (d && !d.pinned ? null : d))}
                style={{
                  position: 'absolute',
                  top: `${yOf(b.start)}px`,
                  left,
                  width: `calc(${laneW} - 3px)`,
                  height: `${height}px`,
                  background: v.bg,
                  border: `1.5px solid ${v.border}`,
                  boxShadow: isClashed ? '0 0 0 2px #f59e0b' : 'none',
                  borderRadius: '0.375rem',
                  padding: '0.125rem 0.375rem',
                  fontSize: '0.625rem',
                  color: v.text,
                  opacity: v.opacity,
                  overflow: 'hidden',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: 700, lineHeight: 1.2, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                  <span style={{ textDecoration: v.strike ? 'line-through' : 'none' }}>
                    {b.appointment_type === 'BLOCK'
                      ? `${b.blocking ? '🔒' : '🔖'} ${b.label || 'Blocked'}${b.is_recurring ? ' 🔁' : ''}`
                      : (b.patient_name || 'Patient')}
                  </span>
                  {v.badge && (
                    <span style={{ marginLeft: '0.25rem', borderRadius: '0.1875rem', padding: '0 0.1875rem', fontSize: '0.5rem', fontWeight: 800, background: v.badge.bg, color: v.badge.fg }}>
                      {v.badge.label}
                    </span>
                  )}
                </div>
                <div style={{ lineHeight: 1.2, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', opacity: 0.95, textDecoration: v.strike ? 'line-through' : 'none' }}>
                  {b.appointment_type === 'BLOCK' && b.all_day
                    ? 'All day'
                    : `${fmtMin(b.start)}–${fmtMin(b.end)}${twoLine && b.appointment_type !== 'BLOCK' ? ` · ${typeLabelOf(b)}` : ''}`}
                </div>
              </div>
            );
          })}

          {/* proposed pick block */}
          {mode === 'pick' && selMin != null && (
            <div style={{
              position: 'absolute',
              top: `${yOf(selMin)}px`,
              left: innerLeft,
              right: '3px',
              height: `${Math.max(propDur * PX_PER_MIN - 2, 22)}px`,
              background: clash ? (isDark ? 'rgba(180,83,9,0.6)' : 'rgba(251,191,36,0.9)') : '#0d9488',
              border: clash ? '2px dashed #b45309' : '2px solid #0f766e',
              borderRadius: '0.375rem',
              padding: '0.125rem 0.375rem',
              fontSize: '0.625rem',
              fontWeight: 700,
              color: clash ? (isDark ? '#fde68a' : '#7c2d12') : '#fff',
              zIndex: 15,
              overflow: 'hidden',
            }}>
              <span style={{ whiteSpace: 'nowrap' }}>
                ● {patientLabel} → {fmtMin(selMin)}{propDur >= 10 ? `–${fmtMin(selMin + propDur)}` : ''} ({propDur}m)
              </span>
              <div
                data-role="resize"
                onMouseDown={startDrag}
                onTouchStart={startDrag}
                style={{
                  position: 'absolute', left: 0, right: 0, bottom: 0, height: coarse ? '20px' : '14px',
                  cursor: 'ns-resize', background: 'rgba(255,255,255,0.4)',
                  borderBottomLeftRadius: '0.375rem', borderBottomRightRadius: '0.375rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <span style={{ width: '20px', height: '3px', borderRadius: '2px', background: 'rgba(0,0,0,0.35)' }} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Detail popover (hover desktop / tap touch) ── */}
      {detail && (
        <>
          {detail.pinned && (
            <div onClick={() => setDetail(null)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
          )}
          <div style={{
            position: 'fixed', left: `${detail.x}px`, top: `${detail.y}px`, zIndex: 41,
            width: '240px', maxWidth: 'calc(100vw - 16px)',
            borderRadius: '0.625rem', padding: '0.625rem 0.75rem',
            background: isDark ? '#0f172a' : '#ffffff',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.12)'}`,
            boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
            color: isDark ? '#e2e8f0' : '#0f172a',
            pointerEvents: detail.pinned ? 'auto' : 'none',
          }}>
            <div style={{ fontSize: '0.8125rem', fontWeight: 700 }}>
              {detail.appt.appointment_type === 'BLOCK' ? `${detail.appt.blocking ? '🔒' : '🔖'} ${detail.appt.label || 'Blocked'}` : (detail.appt.patient_name || 'Patient')}
            </div>
            <div style={{ fontSize: '0.6875rem', marginTop: '0.25rem', color: isDark ? '#94a3b8' : '#475569' }}>
              {detail.appt.appointment_type === 'BLOCK' && detail.appt.all_day
                ? 'All day'
                : `${fmtMin(detail.appt.start)}–${fmtMin(detail.appt.end)} · ${durationOf(detail.appt)} min`}
            </div>
            {detail.appt.appointment_type === 'BLOCK' ? (
              <>
                <div style={{ fontSize: '0.625rem', marginTop: '0.1875rem', color: detail.appt.blocking ? '#dc2626' : (isDark ? '#94a3b8' : '#64748b'), fontWeight: 600 }}>
                  {detail.appt.blocking ? 'Blocking — shown on booking' : 'Calendar note — hidden from booking'}
                </div>
                {detail.appt.is_recurring && (
                  <div style={{ fontSize: '0.625rem', marginTop: '0.1875rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                    🔁 Recurring · {detail.appt.repeat === 'WEEKLY' ? 'Weekly' : 'Daily'}
                  </div>
                )}
              </>
            ) : (
            <div style={{ fontSize: '0.6875rem', marginTop: '0.1875rem', fontWeight: 600 }}>
              {typeLabelOf(detail.appt)}
            </div>
            )}
            {detail.appt.appointment_type !== 'BLOCK' && Array.isArray(detail.appt.procedures) && detail.appt.procedures.length > 0 && (
              <div style={{ marginTop: '0.25rem', display: 'flex', flexDirection: 'column', gap: '0.0625rem' }}>
                {detail.appt.procedures.map((p, i) => (
                  <div key={p.procedure_id || i} style={{ fontSize: '0.625rem', color: isDark ? '#cbd5e1' : '#475569' }}>
                    • {p.name || 'Procedure'}{p.duration_minutes ? ` · ${p.duration_minutes}m` : ''}
                  </div>
                ))}
              </div>
            )}
            {detail.appt.appointment_type !== 'BLOCK' && (
              <div style={{ fontSize: '0.625rem', marginTop: '0.1875rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                Status: {statusLabelOf(detail.appt.status)}
              </div>
            )}
            {detail.appt.notes && (
              <div style={{
                fontSize: '0.6875rem', marginTop: '0.375rem', paddingTop: '0.375rem',
                borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                whiteSpace: 'pre-wrap',
              }}>
                📝 {detail.appt.notes}
              </div>
            )}
            {detail.pinned && (
              <button onClick={() => setDetail(null)} style={{
                marginTop: '0.5rem', width: '100%', padding: '0.3125rem', borderRadius: '0.375rem',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'}`,
                background: isDark ? '#1e293b' : '#f8fafc', color: isDark ? '#e2e8f0' : '#334155',
                fontSize: '0.6875rem', fontWeight: 600, cursor: 'pointer',
              }}>Close</button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
